<template>
  <header class="app-header">
    <div v-if="!user" class="header-content">
      <router-link to="/" class="logo-link">
        <img
          src="@/assets/images/Celestia_header.png"
          alt="Celestia Logo"
          class="logo-image"
        />
      </router-link>
      <nav class="main-nav">
        <a href="/#about">About</a>
        <a href="/#explore">Explore</a>
        <a href="/#community">Community</a>
      </nav>
      <div class="auth-actions">
        <router-link to="/login" class="header-button">Login</router-link>
        <router-link to="/login?action=register" class="header-button secondary"
          >Sign Up</router-link
        >
      </div>
    </div>

    <div v-else class="game-header-content">
      <div class="game-header-bar">
        <button
          type="button"
          class="menu-toggle"
          :aria-expanded="menuOpen"
          aria-controls="header-account-panel"
          aria-label="Account menu"
          @click="toggleMenu"
        >
          <i :class="menuOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
        </button>

        <router-link to="/game/home" class="game-logo-link">
          <img
            src="@/assets/images/Celestia_header.png"
            alt="Celestia Logo"
            class="logo-image"
          />
        </router-link>

        <div class="header-view-action">
          <button
            v-if="headerAction && !menuOpen"
            type="button"
            class="view-action-btn"
            :class="{ active: uiStore.infoDrawerOpen }"
            :aria-pressed="uiStore.infoDrawerOpen"
            :aria-label="headerAction.label"
            @click="toggleViewAction"
          >
            <i :class="headerAction.icon"></i>
            <span>{{ headerAction.label }}</span>
          </button>
        </div>
      </div>

      <div
        id="header-account-panel"
        class="header-account"
        :class="{ open: menuOpen }"
      >
        <div class="player-info">
          <div class="player-avatar">
            <AvatarPortrait
              v-if="user.avatar_id"
              :avatar-id="user.avatar_id"
              :size="40"
            />
            <i v-else class="fas fa-user-circle"></i>
          </div>
          <div class="player-name">
            <span>{{ user.nickname || user.name }}</span>
          </div>
        </div>

        <div class="resources">
          <div class="resource-head" aria-hidden="true">
            <span></span>
            <span>Resource</span>
            <span>Stock</span>
          </div>
          <div
            class="resource-item"
            v-for="resource in resources"
            :key="resource.name"
            :title="resource.name"
          >
            <i :class="resource.icon" :style="{ color: resource.color }"></i>
            <span class="resource-label">{{ resource.name }}</span>
            <span class="resource-value">{{ formatNumber(resource.value) }}</span>
          </div>
        </div>

        <div v-if="user.race" class="audio-settings">
          <div class="audio-settings-head">Audio Settings</div>
          <button
            type="button"
            class="audio-mute-btn"
            :title="uiStore.isAmbiancePlaying ? 'Mute Music' : 'Play Music'"
            @click="toggleRaceAmbiance"
          >
            <i
              :class="
                uiStore.isAmbiancePlaying ? 'fas fa-volume-up' : 'fas fa-volume-mute'
              "
            ></i>
            <span class="audio-status">
              {{ raceThemeLabel }} theme ·
              {{ uiStore.isAmbiancePlaying ? "Playing" : "Muted" }}
            </span>
          </button>
          <label class="audio-volume">
            <span class="audio-volume-label">Volume</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              :value="uiStore.userVolume"
              aria-label="Music volume"
              @input="uiStore.setAmbianceVolume($event.target.value)"
            />
          </label>
        </div>

        <div class="game-info">
          <div class="realm-time">
            <span>Day {{ dayOfYear }}</span>
            <span>{{ currentTime }}</span>
          </div>
          <button @click="handleLogout" class="logout-btn" title="Logout">
            <i class="fas fa-sign-out-alt"></i>
            <span class="logout-label">Logout</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="menuOpen"
      class="header-menu-backdrop"
      @click="closeMenu"
    ></div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "../store/auth";
import { useUiStore } from "../store/ui";
import { useGameStore } from "../store/game";
import {
  resourceDisplayMap,
  HEADER_RESOURCE_KEYS,
  projectResourceStocks,
} from "../utils/resourceDisplay";
import AvatarPortrait from "./AvatarPortrait.vue";
import { getRaceThemeLabel, toggleRaceAmbiance } from "../utils/ambiance";

const MOBILE_MAX_WIDTH = 768;

const authStore = useAuthStore();
const uiStore = useUiStore();
const gameStore = useGameStore();
const router = useRouter();
const route = useRoute();
const user = computed(() => authStore.user);
const menuOpen = ref(false);
const raceThemeLabel = computed(() => getRaceThemeLabel(user.value?.race));

const globalAggregation = computed(() => gameStore.globalAggregation);
const tickIntervalMs = computed(() => gameStore.tickIntervalMs || 60 * 60 * 1000);

const visualResources = ref({});
let tickerInterval = null;

const headerAction = computed(() => {
  const name = route.name;
  if (name === "GameQuest" && (gameStore.playerHeroes?.length || 0) > 0) {
    return { label: "Units", icon: "fas fa-users" };
  }
  if (name === "GameCity" && gameStore.activeCity) {
    return { label: "Info", icon: "fas fa-landmark" };
  }
  if (name === "GameHero" && gameStore.activeHero) {
    return { label: "Stats", icon: "fas fa-user-shield" };
  }
  if (name === "GameBattle") {
    return { label: "Targets", icon: "fas fa-bullseye" };
  }
  return null;
});

function closeMenu() {
  menuOpen.value = false;
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) {
    uiStore.setInfoDrawerOpen(false);
  }
}

function toggleViewAction() {
  closeMenu();
  uiStore.setInfoDrawerOpen(!uiStore.infoDrawerOpen);
}

function onKeydown(event) {
  if (event.key === "Escape") closeMenu();
}

function onResize() {
  if (window.innerWidth > MOBILE_MAX_WIDTH) closeMenu();
}

function projectResources() {
  const base = globalAggregation.value;
  if (!base) return {};
  const projected = projectResourceStocks(
    base,
    base.rates,
    base.last_ticked_at,
    gameStore.clockOffsetMs,
    tickIntervalMs.value
  );
  const header = {};
  for (const key of HEADER_RESOURCE_KEYS) {
    header[key] = projected[key];
  }
  return header;
}

function refreshVisualResources() {
  visualResources.value = projectResources();
}

watch(
  () => [globalAggregation.value, gameStore.clockOffsetMs],
  () => {
    refreshVisualResources();
  },
  { immediate: true, deep: true }
);

watch(
  () => route.fullPath,
  () => {
    closeMenu();
  }
);

const startTicker = () => {
  if (tickerInterval) clearInterval(tickerInterval);
  tickerInterval = setInterval(refreshVisualResources, 1000);
};

// --- Time and Day ---
const currentTime = ref("");
const dayOfYear = ref(0);
let timerInterval = null;

const updateTime = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  dayOfYear.value = Math.floor(diff / (1000 * 60 * 60 * 24));
};

onMounted(() => {
  updateTime();
  timerInterval = setInterval(updateTime, 1000 * 60);
  startTicker();
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onResize);
});

onUnmounted(() => {
  clearInterval(timerInterval);
  if (tickerInterval) clearInterval(tickerInterval);
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onResize);
});

const resources = computed(() => {
  const source = visualResources.value.gold !== undefined ? visualResources.value : (globalAggregation.value || {});

  if (!source || Object.keys(source).length === 0) {
    return HEADER_RESOURCE_KEYS.map((key) => ({
      ...resourceDisplayMap[key],
      value: 0,
    }));
  }

  return HEADER_RESOURCE_KEYS.map((key) => {
    const config = resourceDisplayMap[key];
    const value = Math.max(0, Math.floor(Number(source[key]) || 0));
    return { ...config, value: value };
  });
});

const handleLogout = async () => {
  closeMenu();
  uiStore.stopAudio();
  await authStore.logout();
  router.push("/");
};

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num;
};
</script>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

/* Base Header Style */
.app-header {
  background-color: rgba(10, 10, 20, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 246, 255, 0.5);
  padding: 1rem 2rem;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  height: 70px;
  box-sizing: border-box;
}

/* Marketing Header Content */
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}
.logo-link .logo-image,
.game-logo-link .logo-image {
  height: 40px;
}
.main-nav {
  display: flex;
  gap: 2rem;
}
.main-nav a {
  color: #e0e0e0;
  text-decoration: none;
  font-family: "Exo 2", sans-serif;
  transition: color 0.3s ease, text-shadow 0.3s ease;
}
.main-nav a:hover {
  color: #00f6ff;
  text-shadow: 0 0 10px #00f6ff;
}
.auth-actions {
  display: flex;
  gap: 1rem;
}
.header-button {
  font-family: "Orbitron", sans-serif;
  background: transparent;
  color: #00f6ff;
  border: 1px solid #00f6ff;
  padding: 8px 16px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease-in-out;
}
.header-button:hover {
  background-color: rgba(0, 246, 255, 0.2);
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
}
.header-button.secondary {
  background-color: rgba(0, 246, 255, 0.1);
}

/* In-Game Header Content */
.game-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  color: #e0e0e0;
}

.game-header-bar,
.game-logo-link,
.menu-toggle,
.header-view-action,
.logout-label,
.resource-label,
.resource-head,
.audio-settings {
  display: none;
}

.header-account {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.player-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-family: "Exo 2", sans-serif;
}
.player-avatar {
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 246, 255, 0.5);
  flex-shrink: 0;
}
.player-avatar :deep(.avatar-portrait) {
  width: 100% !important;
  height: 100% !important;
}
.player-avatar i {
  font-size: 2.5rem;
  color: #00f6ff;
}
.player-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: bold;
}

.resources {
  display: flex;
  gap: 2rem;
  font-family: "Orbitron", sans-serif;
}
.resource-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.resource-item i {
  font-size: 1.2rem;
}
.resource-value {
  font-weight: bold;
  font-size: 1.1rem;
}

.game-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.realm-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: "Exo 2", sans-serif;
  font-size: 0.9rem;
}
.logout-btn {
  background: transparent;
  border: 1px solid #00f6ff;
  color: #00f6ff;
  border-radius: 5px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.logout-btn:hover {
  background-color: rgba(0, 246, 255, 0.2);
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
}

@media (max-width: 768px) {
  .app-header {
    height: 56px;
    padding: 0 12px;
  }

  .main-nav {
    display: none;
  }

  .header-content {
    justify-content: space-between;
  }

  .auth-actions {
    gap: 0.5rem;
  }

  .header-button {
    padding: 6px 12px;
    font-size: 14px;
  }

  .game-header-content {
    display: block;
    width: 100%;
    height: 100%;
  }

  .game-header-bar {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    height: 56px;
  }

  .game-logo-link {
    display: flex;
    align-items: center;
    justify-content: center;
    justify-self: center;
    flex-shrink: 0;
  }

  .game-logo-link .logo-image {
    height: 32px;
  }

  .menu-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    justify-self: start;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    background: transparent;
    border: 1px solid #00f6ff;
    color: #00f6ff;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1.2rem;
    z-index: 3;
  }

  .menu-toggle:hover {
    background-color: rgba(0, 246, 255, 0.2);
    box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
  }

  .header-view-action {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    justify-self: end;
    min-height: 40px;
  }

  .view-action-btn {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    height: 36px;
    padding: 0 0.7rem;
    background: rgba(10, 15, 30, 0.92);
    border: 1px solid #00f6ff;
    border-radius: 999px;
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
    font-size: 0.68rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 0 12px rgba(0, 246, 255, 0.35);
  }

  .view-action-btn.active {
    background: rgba(0, 246, 255, 0.2);
    box-shadow: 0 0 14px rgba(0, 246, 255, 0.5);
  }

  .header-account {
    display: none;
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 1rem;
    padding: 1rem 0.9rem 1.15rem;
    background: rgba(10, 15, 30, 0.97);
    border-bottom: 1px solid #00f0ff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
    max-height: calc(100dvh - 56px - 80px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    z-index: 2;
    box-sizing: border-box;
  }

  .header-account.open {
    display: grid;
    grid-template-areas:
      "player"
      "time"
      "resources"
      "audio"
      "logout";
  }

  .header-account.open .player-info {
    grid-area: player;
  }

  .header-account.open .game-info {
    display: contents;
  }

  .header-account.open .realm-time {
    grid-area: time;
  }

  .header-account.open .resources {
    grid-area: resources;
  }

  .header-account.open .audio-settings {
    grid-area: audio;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    width: min(100%, 22rem);
    margin: 0 auto;
    padding: 0.75rem 0.9rem;
    box-sizing: border-box;
    border: 1px solid rgba(0, 240, 255, 0.28);
    border-radius: 8px;
    background: rgba(0, 5, 15, 0.55);
    box-shadow: inset 0 0 12px rgba(0, 240, 255, 0.06);
  }

  .header-account.open .logout-btn {
    grid-area: logout;
  }

  .audio-settings-head {
    font-family: "Cinzel", serif;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #c9b48a;
  }

  .audio-mute-btn {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    width: 100%;
    min-height: 44px;
    padding: 0.55rem 0.75rem;
    background: transparent;
    border: 1px solid #00f6ff;
    border-radius: 5px;
    color: #00f6ff;
    cursor: pointer;
    font-family: "Exo 2", sans-serif;
    text-align: left;
  }

  .audio-mute-btn:hover {
    background-color: rgba(0, 246, 255, 0.2);
    box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
  }

  .audio-mute-btn i {
    font-size: 1.1rem;
    width: 1.25rem;
    text-align: center;
    flex-shrink: 0;
  }

  .audio-status {
    font-size: 0.84rem;
    font-weight: 600;
    color: #d8dee9;
  }

  .audio-volume {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .audio-volume-label {
    font-family: "Exo 2", sans-serif;
    font-size: 0.78rem;
    color: #88c0d0;
    flex-shrink: 0;
  }

  .audio-volume input[type="range"] {
    flex: 1;
    min-width: 0;
    accent-color: #00f6ff;
    cursor: pointer;
  }

  .header-menu-backdrop {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1;
  }

  .player-info {
    gap: 0.75rem;
  }

  .player-avatar {
    width: 48px;
    height: 48px;
  }

  .player-avatar i {
    font-size: 2.2rem;
  }

  .player-name {
    font-size: 1.05rem;
    max-width: none;
    min-width: 0;
    overflow: visible;
  }

  .player-name span {
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
    word-break: break-word;
  }

  .resources {
    flex-direction: column;
    gap: 0;
    overflow: hidden;
    white-space: normal;
    width: min(100%, 22rem);
    margin: 0 auto;
    border: 1px solid rgba(0, 240, 255, 0.28);
    border-radius: 8px;
    background: rgba(0, 5, 15, 0.55);
    box-shadow: inset 0 0 12px rgba(0, 240, 255, 0.06);
  }

  .resource-head,
  .resource-item {
    display: grid;
    grid-template-columns: 2.25rem minmax(0, 1fr) 5.25rem;
    align-items: center;
    column-gap: 0.65rem;
    width: 100%;
    padding: 0.55rem 0.9rem;
    box-sizing: border-box;
  }

  .resource-head {
    font-family: "Cinzel", serif;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #c9b48a;
    background: rgba(0, 240, 255, 0.1);
    border-bottom: 1px solid rgba(0, 240, 255, 0.25);
  }

  .resource-head span:last-child {
    text-align: right;
  }

  .resource-item {
    gap: 0;
    background: transparent;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid rgba(0, 240, 255, 0.12);
  }

  .resource-item:nth-child(odd) {
    background: rgba(0, 240, 255, 0.04);
  }

  .resource-item:last-child {
    border-bottom: none;
  }

  .resource-item i {
    font-size: 1rem;
    width: auto;
    justify-self: center;
    text-align: center;
  }

  .resource-label {
    display: block;
    font-family: "Exo 2", sans-serif;
    font-size: 0.84rem;
    font-weight: 600;
    color: #d8dee9;
    text-align: left;
  }

  .resource-value {
    font-size: 0.95rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #00f0ff;
  }

  .game-info {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .realm-time {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    font-size: 0.82rem;
    color: #88c0d0;
  }

  .logout-btn {
    width: 100%;
    height: 44px;
    font-size: 0.95rem;
    font-family: "Orbitron", sans-serif;
  }

  .logout-label {
    display: inline;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
}
</style>
